/**
 * 图形验证码。
 *
 * 后端 `GET /auth/captcha` 直接返回 SVG 源码（见 auth.routes.ts），所以这里
 * 内联渲染，省掉一次图片请求；点击即可换一张。
 * SVG 来自自家后端的 captcha 库，不含用户输入，直接 dangerouslySetInnerHTML 是可控的。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as authApi from '../lib/api/auth';
import { toErrorMessage } from '../lib/api/error';
import { cn } from '../lib/utils';

export interface CaptchaFieldProps {
  /** 每次刷新后把新的 captchaId 交给父组件，提交时一起带上。 */
  onChange: (captchaId: string | null) => void;
  /** 外部触发刷新（如验证码答错后自动换一张）。 */
  refreshToken?: number;
  kid?: boolean;
}

export function CaptchaField({ onChange, refreshToken = 0, kid = false }: CaptchaFieldProps) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // 用 ref 持有最新的 onChange：否则父组件每次 render 都会让 load 变化，
  // 触发下面的 useEffect，导致验证码无限重新拉取。
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await authApi.fetchCaptcha();
      setSvg(data.svg);
      onChangeRef.current(data.captchaId);
    } catch (err) {
      setSvg(null);
      onChangeRef.current(null);
      setError(toErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  return (
    <div className="space-y-1.5">
      <span className={cn('block font-medium text-slate-700', kid ? 'text-lg' : 'text-sm')}>
        图形验证码
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void load()}
          title="点击换一张"
          aria-label="刷新图形验证码"
          className={cn(
            'flex items-center justify-center overflow-hidden rounded-xl border border-slate-300 bg-white',
            kid ? 'h-[56px] w-32' : 'h-11 w-28',
          )}
        >
          {loading && !svg ? (
            <span className="text-xs text-slate-400">加载中…</span>
          ) : svg ? (
            // SVG 自带尺寸，这里限制最大尺寸避免撑破布局
            <span
              className="block h-full w-full [&>svg]:h-full [&>svg]:w-full"
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          ) : (
            <span className="px-2 text-center text-xs text-slate-400">点击重试</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-lg px-2 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50"
        >
          换一张
        </button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-rose-600">
          {error}
        </p>
      )}
    </div>
  );
}
